import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NOT_APPLICABLE_STRING } from '../../../../constants/general';
import { SpaceType } from '../../../../constants/space';
import { StationItemNo } from '../../../../constants/Games/gameStation';
import { getStationVisual } from '../../../../constants/Games/gameStationUtils';
import { SVGIconType } from '../../../../constants/svgIcon';
import { Profile } from '../../../../models/UserStore';
import { Space } from '../../../../models/SpaceStore';
import { Album, AlbumAudience } from '../../../../models/SpaceStore/Album';
import { timeAgo } from '../../../../utils/date';

import { AvatarNode } from '../../AvatarNode';
import { SeparatorComponent } from '../../SeparatorComponent';
import { Skeleton } from '../../Skeleton';
import { SvgIcon } from '../../SVGIcon';
import './StartStationCell.css';

export type StartStationCellProps = {
  album: Album;
  albumClassroom: Space;
  classroomOwnerProfile?: Profile | undefined | null;
  stationId: StationItemNo;
  name: string | undefined;
  isUnlocked: boolean;
  onClickPlay?: () => void;
};

export const StartStationCell = memo((props: StartStationCellProps) => {
  const {
    album,
    albumClassroom,
    classroomOwnerProfile,
    stationId,
    name,
    isUnlocked,
  } = props;
  const { t, i18n } = useTranslation();

  const albumInfo =
    album?.i18nGameSetInfo.resources[album?.i18nGameSetInfo.defaultLocale];

  const [isAlbumClassroomIconAvailable, setIsAlbumClassroomIconAvailable] =
    useState(true);

  return (
    <div className="start-station-cell">
      <div className="centered-container">
        <div className="lower-row">
          <div className="component">
            <img
              src={getStationVisual(stationId)?.filePrefix}
              className="large-icon"
              alt="Large Icon"
            />
            <div className="text-overlay">{name ?? t('Start')}</div>
          </div>
          <div
            className={`game-component ${isUnlocked ? 'unlocked' : 'locked'}`}
          >
            <div className="upper-part">
              <div className="left-part">
                <div className="title">{albumInfo?.name}</div>
                <div className="info">
                  <SeparatorComponent
                    separator={<p className={`separate-text grey-text`}>·</p>}
                  >
                    <div className="text-component">
                      {classroomOwnerProfile &&
                      albumClassroom?.type == SpaceType.Home ? (
                        <>
                          <AvatarNode
                            avatar={{
                              ...classroomOwnerProfile.avatar,
                              userId: classroomOwnerProfile.userId,
                              source:
                                classroomOwnerProfile.profilePicture &&
                                classroomOwnerProfile.profilePicture.publicUrls
                                  ?.small
                                  ? {
                                      uri: classroomOwnerProfile.profilePicture
                                        .publicUrls.small,
                                    }
                                  : NOT_APPLICABLE_STRING,
                            }}
                            size={32}
                          />
                        </>
                      ) : albumClassroom?.icon?.publicUrls?.small &&
                        isAlbumClassroomIconAvailable ? (
                        <img
                          src={albumClassroom?.icon?.publicUrls?.small}
                          onError={() =>
                            setIsAlbumClassroomIconAvailable(false)
                          }
                          className="space-icon"
                        />
                      ) : (
                        <Skeleton
                          width={'32px'}
                          height={'32px'}
                          borderRadius={'1rem'}
                        />
                      )}

                      <div className={`small-grey-text grey-text`}>
                        {
                          albumClassroom?.i18nClassroomInfo.resources[
                            albumClassroom.i18nClassroomInfo.defaultLocale
                          ]?.name
                        }
                      </div>
                    </div>
                    {album ? (
                      <div className={`small-grey-text grey-text`}>
                        {timeAgo(album.createdAt, i18n.language)}
                      </div>
                    ) : null}

                    {album ? (
                      <SvgIcon
                        name={
                          album.audience == AlbumAudience.Public
                            ? SVGIconType.Region
                            : album.audience == AlbumAudience.Members
                            ? SVGIconType.Member
                            : album.audience == AlbumAudience.Followers
                            ? SVGIconType.Friend
                            : SVGIconType.MembershipTier
                        }
                        className={`privacy-icon grey-text`}
                      />
                    ) : null}

                    {album &&
                    album.statistics.gamePlayCount &&
                    album.statistics.gamePlayCount > 0 ? (
                      <div className={`small-grey-text grey-text`}>
                        {t('{count} Plays', {
                          count: 1,
                        })}
                      </div>
                    ) : null}
                  </SeparatorComponent>
                </div>
              </div>
              {isUnlocked ? (
                <div className="right-part">
                  <div className="play-button">
                    <SvgIcon
                      name={SVGIconType.Play}
                      width={24}
                      height={24}
                      stroke="#00bfa5"
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
